import type { ProductModule, WaitingOnOption } from '@erp/shared-types';
import { useGetModuleConfig } from '@/api/org/config/modules';

/** The module-config payload the reader below is handed. */
type ModuleConfigValue = NonNullable<
  ReturnType<typeof useGetModuleConfig>['data']
>['config'];

/**
 * Where one product module keeps its "waiting on" taxonomy — the client-side
 * twin of the backend's `WaitingOnCatalogSource`, resolving against the same
 * org module config so the picker and the server-side validation can never
 * disagree about the option list.
 */
export interface WaitingOnOptionsSource {
  /** The module whose org config carries the configured option list. */
  module: ProductModule;
  /**
   * Reads the configured list out of that module's config. Returns `undefined`
   * when the org has never configured one — only then do the defaults apply.
   */
  readConfiguredOptions: (
    config: ModuleConfigValue | undefined,
  ) => WaitingOnOption[] | undefined;
  /** Seeded taxonomy applied when nothing is configured. */
  defaultOptions: readonly WaitingOnOption[];
}

/**
 * The org's configured "waiting on" option list (Set Next Step reasons) for one
 * module, falling back to that module's seeded defaults when unconfigured.
 * Backed by the shared, cached module-config query, so per-row cells can call
 * this freely — every consumer reads the same query instance.
 *
 * Returns ALL options including disabled ones: stored keys on old records must
 * keep resolving to labels. Filter on `enabled` for pickers.
 */
export function useWaitingOnOptions(
  source: WaitingOnOptionsSource,
): WaitingOnOption[] {
  const query = useGetModuleConfig(source.module);
  const configured = source.readConfiguredOptions(query.data?.config);
  // No manual memo: React Compiler caches this value itself.
  return configured ?? [...source.defaultOptions];
}
